// src/components/GameMediaSlider.js
import React, { useState, useRef, useEffect } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { FreeMode, Navigation, Thumbs } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/free-mode';
import 'swiper/css/navigation';
import 'swiper/css/thumbs';
import { FaRegImage, FaPlay, FaChevronLeft, FaChevronRight } from 'react-icons/fa';

// 간단한 해시 함수 (URL → 짧은 문자열)
function hashString(str) {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = (hash << 5) - hash + str.charCodeAt(i);
    hash |= 0;
  }
  return 'h' + Math.abs(hash);
}

export default function GameMediaSlider({ media = [] }) {
  const [thumbsSwiper, setThumbsSwiper] = useState(null);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [cacheMap, setCacheMap] = useState({}); // { idx: dataURL }
  const mainSwiperRef = useRef();
  const videoRefs = useRef([]);

  // 이미지 → DataURL 변환 및 캐시
  const cacheImage = async (url, key) => {
    try {
      const res = await fetch(url);
      const blob = await res.blob();
      const reader = new FileReader();
      reader.onloadend = () => {
        const dataUrl = reader.result;
        localStorage.setItem(key, dataUrl);
        setCacheMap(prev => ({ ...prev, [key]: dataUrl }));
      };
      reader.readAsDataURL(blob);
    } catch (e) {
      console.warn('이미지 캐싱 실패', url, e);
    }
  };

  // 비디오 첫 프레임 추출 함수
  const extractFrame = (source) =>
    new Promise((resolve) => {
      const vid = document.createElement('video');
      vid.crossOrigin = 'anonymous';
      vid.muted = true;
      vid.preload = 'metadata';
      vid.src = source;
      vid.addEventListener('loadedmetadata', () => {
        vid.currentTime = 0.1;
      });
      vid.addEventListener('seeked', () => {
        const canvas = document.createElement('canvas');
        canvas.width = vid.videoWidth;
        canvas.height = vid.videoHeight;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(vid, 0, 0);
        const dataUrl = canvas.toDataURL('image/png');
        resolve(dataUrl);
      });
      vid.addEventListener('error', () => resolve(null));
      vid.load();
    });

  // media 변경 시 캐시 확인 및 생성
  useEffect(() => {
    media.forEach((item, idx) => {
      const key = `mediaCache_${hashString(item.url)}`;
      const stored = localStorage.getItem(key);
      if (stored) {
        setCacheMap(prev => ({ ...prev, [idx]: stored }));
      } else {
        if (item.type === 'image') {
          cacheImage(item.url, key);
        } else if (item.type === 'video') {
          extractFrame(item.url).then((dataUrl) => {
            if (dataUrl) {
              localStorage.setItem(key, dataUrl);
              setCacheMap(prev => ({ ...prev, [idx]: dataUrl }));
            }
          });
        }
      }
    });
  }, [media]);

  // 메인 슬라이드 변경 핸들러
  const onMainSlideChange = (swiper) => {
    const idx = swiper.realIndex;
    setCurrentIdx(idx);
    videoRefs.current.forEach((v, i) => {
      if (!v) return;
      if (i === idx && media[i].type === 'video') {
        v.play().catch(() => {});
      } else {
        v.pause();
      }
    });
  };

  const goPrev = () => mainSwiperRef.current?.slidePrev();
  const goNext = () => mainSwiperRef.current?.slideNext();

  // 로드 직후 첫 비디오 재생
  useEffect(() => {
    const firstVideo = videoRefs.current[0];
    if (firstVideo && media[0]?.type === 'video') {
      firstVideo.play().catch(() => {});
    }
  }, [media]);

  return (
    <div className="space-y-6 bg-neutral-800 p-6 rounded-xl">
      {/* 메인 슬라이더 */}
      <Swiper
        modules={[Navigation, Thumbs]}
        onSwiper={(s) => (mainSwiperRef.current = s)}
        thumbs={{ swiper: thumbsSwiper && !thumbsSwiper.destroyed ? thumbsSwiper : null }}
        navigation={false}
        loop={media.length > 1}
        onSlideChange={onMainSlideChange}
        className="w-full aspect-video rounded-lg overflow-hidden"
      >
        {media.length === 0 && (
          <div className="flex items-center justify-center w-full h-full text-gray-500">
            <FaRegImage size={64} />
          </div>
        )}
        {media.map((item, idx) => (
          <SwiperSlide key={idx}>
            {item.type === 'video' ? (
              <video
                ref={(el) => {
                  if (el) {
                    el.muted = true;
                    el.playsInline = true;
                  }
                  videoRefs.current[idx] = el;
                }}
                src={item.url}
                controls
                loop
                autoPlay
                className="w-full h-full object-cover"
              />
            ) : (
              <img
                src={cacheMap[idx] || item.url}
                alt={`media-${idx}`}
                className="w-full h-full object-cover"
              />
            )}
          </SwiperSlide>
        ))}
      </Swiper>

      {/* 썸네일 + Prev/Next 버튼 */}
      <div className="relative flex items-center">
        <button
          onClick={goPrev}
          disabled={media.length === 0}
          className={`z-10 text-white bg-black bg-opacity-50 p-2 rounded-full transition ${
            media.length === 0 ? 'opacity-30 cursor-not-allowed' : 'hover:bg-opacity-75'
          }`}
        >
          <FaChevronLeft />
        </button>

        <div className="flex-1 overflow-hidden px-6">
          <Swiper
            modules={[FreeMode, Thumbs]}
            onSwiper={setThumbsSwiper}
            spaceBetween={8}
            slidesPerView={5}
            freeMode
            watchSlidesProgress
          >
            {media.length === 0 ? (
              <SwiperSlide>
                <div className="w-28 aspect-video rounded border-2 border-dashed border-gray-700 flex items-center justify-center text-gray-500">
                  <FaRegImage />
                </div>
              </SwiperSlide>
            ) : (
              media.map((item, idx) => {
                const thumbUrl = cacheMap[idx] || item.url;
                return (
                  <SwiperSlide key={idx} className="cursor-pointer">
                    <div
                      onClick={() => mainSwiperRef.current?.slideToLoop(idx)}
                      className={`relative w-full h-0 pb-[56.25%] rounded-xl overflow-hidden border-2 transition-colors ${
                        idx === currentIdx ? 'border-orange-500' : 'border-transparent'
                      }`}
                    >
                      {thumbUrl ? (
                        <img
                          src={thumbUrl}
                          alt={`thumb-${idx}`}
                          className="absolute inset-0 w-full h-full object-cover"
                        />
                      ) : (
                        <div className="absolute inset-0 flex items-center justify-center bg-gray-700">
                          <FaRegImage className="text-2xl text-gray-500" />
                        </div>
                      )}
                      {item.type === 'video' && thumbUrl && (
                        <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-10">
                          <FaPlay className="text-white text-xl" />
                        </div>
                      )}
                    </div>
                  </SwiperSlide>
                );
              })
            )}
          </Swiper>
        </div>

        <button
          onClick={goNext}
          disabled={media.length === 0}
          className={`z-10 text-white bg-black bg-opacity-50 p-2 rounded-full transition ${
            media.length === 0 ? 'opacity-30 cursor-not-allowed' : 'hover:bg-opacity-75'
          }`}
        >
          <FaChevronRight />
        </button>
      </div>
    </div>
  );
}
